import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { MockBadge, Button, Spinner } from "../components/ui";
import { Activity, AlertCircle, CheckCircle2, RefreshCw, Power } from "lucide-react";
import { useOutletContext } from "react-router-dom";
import { useI18n } from "../i18n/I18nContext";

export default function Diagnostics() {
  const { t } = useI18n();
  const [data, setData] = useState(null);
  const [live, setLive] = useState(null);
  const [busy, setBusy] = useState(false);
  const { setLiveMode } = useOutletContext() || {};

  const refresh = async () => {
    setBusy(true);
    try {
      const [d, l] = await Promise.all([
        api.get("/admin/diagnostics"),
        api.get("/admin/live-mode"),
      ]);
      setData(d.data);
      setLive(l.data);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const toggle = async (enabled) => {
    const { data } = await api.post("/admin/live-mode", { enabled });
    setLive(data);
    if (setLiveMode) setLiveMode(data);  // sync topbar chip
  };

  if (!data) return <div className="p-10"><Spinner /></div>;

  return (
    <div className="reveal">
      <div className="px-6 md:px-10 pt-8 pb-5 border-b border-border bg-grid flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="overline">{t("diagnostics.eyebrow")}</div>
          <h1 className="mt-2 font-heading text-4xl font-black tracking-tight">{t("diagnostics.title")}</h1>
          <p className="mt-2 text-xs text-text-muted">
            {t("diagnostics.subtitle")}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button data-testid="diag-refresh" onClick={refresh} variant="ghost" disabled={busy}>
            {busy ? <Spinner /> : <RefreshCw size={14} />} {t("diagnostics.rerunChecks")}
          </Button>
          <MockBadge />
        </div>
      </div>

      <div className="grid grid-cols-12">
        {/* Live mode toggle */}
        <section className="col-span-12 lg:col-span-5 border-r border-border p-6">
          <div className="overline">{t("diagnostics.liveMode")}</div>
          <h3 className="mt-1 font-heading text-2xl font-bold">{t("diagnostics.pipelineSource")}</h3>

          <div className="mt-4 border border-border bg-surface p-5">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="overline">{t("diagnostics.currently")}</div>
                <div className={`mt-1 num text-3xl ${live?.effective ? "text-signal-green" : "text-signal-yellow"}`}>
                  {live?.effective ? "LIVE" : "SYNTH"}
                </div>
                <div className="mt-1 text-[10px] font-mono text-text-muted">
                  env={String(live?.env_use_live_screen)} · override={String(live?.runtime_override)}
                </div>
              </div>
              <Power size={28} className={live?.effective ? "text-signal-green" : "text-text-muted"} />
            </div>

            <div className="mt-5 grid grid-cols-3 gap-1">
              <button
                onClick={() => toggle(true)}
                data-testid="live-mode-on"
                className={`py-2 font-mono text-[10px] uppercase tracking-widest2 border transition-colors ${
                  live?.runtime_override === true
                    ? "border-signal-green text-signal-green bg-signal-green/10"
                    : "border-border text-text-muted hover:text-white"
                }`}
              >
                {t("diagnostics.forceOn")}
              </button>
              <button
                onClick={() => toggle(false)}
                data-testid="live-mode-off"
                className={`py-2 font-mono text-[10px] uppercase tracking-widest2 border transition-colors ${
                  live?.runtime_override === false
                    ? "border-signal-yellow text-signal-yellow bg-signal-yellow/10"
                    : "border-border text-text-muted hover:text-white"
                }`}
              >
                {t("diagnostics.forceOff")}
              </button>
              <button
                onClick={() => toggle(null)}
                data-testid="live-mode-env"
                className={`py-2 font-mono text-[10px] uppercase tracking-widest2 border transition-colors ${
                  live?.runtime_override === null
                    ? "border-white text-white bg-white/5"
                    : "border-border text-text-muted hover:text-white"
                }`}
              >
                {t("diagnostics.followEnv")}
              </button>
            </div>
          </div>

          <div className="mt-6 border border-border p-4">
            <div className="overline">{t("diagnostics.toGoLive")}</div>
            <ol className="mt-2 list-decimal pl-4 text-xs text-text-secondary space-y-1.5 font-mono">
              <li>{t("diagnostics.step1", { country: "AT" })}</li>
              <li>{t("diagnostics.step2", { from: "/app/config.toml.example", to: "/app/config.toml" })}</li>
              <li>{t("diagnostics.step3", { cmd: "pip install beautifulsoup4 lxml" })}</li>
              <li>{t("diagnostics.step4", { env: "USE_LIVE_SCREEN=1" })}</li>
              <li>{t("diagnostics.step5", { cmd: "supervisorctl restart backend" })}</li>
              <li>{t("diagnostics.step6")}</li>
            </ol>
          </div>
        </section>

        {/* Check grid */}
        <section className="col-span-12 lg:col-span-7 p-6">
          <div className="flex items-end justify-between">
            <div>
              <div className="overline">{t("diagnostics.readinessChecks")}</div>
              <h3 className="mt-1 font-heading text-2xl font-bold flex items-center gap-3">
                <Activity size={22} /> {t("diagnostics.greenOfTotal", { ok: data.checks.filter(c => c.ok).length, total: data.checks.length })}
              </h3>
            </div>
            <div className={`px-3 py-1.5 font-mono text-xs uppercase tracking-widest2 border ${
              data.live_ready
                ? "border-signal-green text-signal-green bg-signal-green/10"
                : "border-signal-red text-signal-red bg-signal-red/10"
            }`}>
              {data.live_ready ? t("diagnostics.ready") : t("diagnostics.notReady")}
            </div>
          </div>

          <div className="mt-5 space-y-2">
            {data.checks.map((c) => (
              <div
                key={c.id}
                data-testid={`diag-check-${c.id}`}
                className={`border ${c.ok ? "border-signal-green/30 bg-signal-green/5" : "border-signal-red/30 bg-signal-red/5"} p-4`}
              >
                <div className="flex items-start gap-3">
                  {c.ok ? <CheckCircle2 size={18} className="text-signal-green mt-0.5" /> : <AlertCircle size={18} className="text-signal-red mt-0.5" />}
                  <div className="flex-1 min-w-0">
                    <div className="font-mono text-sm text-white">{c.label}</div>
                    <div className="mt-1 text-[11px] font-mono text-text-secondary break-all">{c.detail}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-5 text-[10px] font-mono text-text-muted">
            {t("diagnostics.ranAt", { time: data.ran_at, env: data.env?.USE_LIVE_SCREEN || t("diagnostics.unset") })}
          </div>
        </section>
      </div>
    </div>
  );
}
